import React, {useEffect, useState} from 'react';
import {GridColDef, GridRowParams} from "@mui/x-data-grid";
import {PlayerInfoDto} from "sandy-shared/dist/dtos/player.dto";
import {Filter} from "../../shared/types/filter";
import {UrlFilterAwareDataGrid} from "../../shared/components/UrlFilterAwareDataGrid";
import {Box, Button, Modal} from "@mui/material";
import PlayerManagementPlayerComponent from "./PlayerManagementPlayerComponent";
import {getPlayers, refreshAllPlayersStats} from "../../services/player.service";
import {enqueueSnackbar} from "notistack";
import {Season} from "sandy-shared/dist/types/season-info.type";

const showAllFilter: Filter<PlayerInfoDto> = {
  displayName: 'All',
  urlParam: 'showAll',
  filterFunction: (values: PlayerInfoDto[]) => values
}
const allFilters = [showAllFilter];
const defaultFilter = showAllFilter;

const playerColumns: GridColDef[] = [
  { field: 'name', headerName: 'Name', width: 180 },
  { field: 'school', headerName: 'School', width: 130 },
  { field: 'sideoutOpportunities', headerName: 'Sideout Opportunities', width: 170 },
  { field: 'sideoutPercentage', headerName: 'Sideout %', width: 110 },
  { field: 'fbSideoutPercentage', headerName: 'FB Sideout %', width: 130 },
  { field: 'fbEfficiency', headerName: 'FB Efficiency', width: 120 },
  { field: 'receiveTransWinPercentage', headerName: 'Receive Trans. Win %', width: 170 },
  { field: 'kills', headerName: 'Kills', width: 80 },
  { field: 'errors', headerName: 'Errors', width: 90 },
  { field: 'aced', headerName: 'Aced', width: 80 },
  { field: 'totalServes', headerName: 'Total Serves', width: 120 },
  { field: 'serveKOPercentage', headerName: 'Serve KO %', width: 120 },
  { field: 'acePercentage', headerName: 'Ace %', width: 100 },
  { field: 'serveEfficiency', headerName: 'Serve Efficiency %', width: 150 },
  { field: 'serveTransWinPercentage', headerName: 'Serve Trans Win %', width: 160 },
  { field: 'aces', headerName: 'Aces', width: 80 },
  { field: 'serviceErrors', headerName: 'Service Errors', width: 130 }
];

const playerRowBuilder = (player: PlayerInfoDto) => {
  return {
    id: player.id,
    name: `${player.firstName} ${player.lastName}`,
    school: player.school,
    sideoutOpportunities: player.sideout.opportunities,
    sideoutPercentage: player.sideout.percentage.toFixed(3),
    fbSideoutPercentage: player.firstBallSideout.percentage.toFixed(3),
    fbEfficiency: player.firstBallSideout.efficiencyPercentage?.toFixed(3) ?? '0.000',
    receiveTransWinPercentage: player.sideout.transitionWinPercentage.toFixed(3),
    kills: player.firstBallSideout.kills,
    errors: player.firstBallSideout.errors,
    aced: player.sideout.aced,
    totalServes: player.serving.opportunities,
    serveKOPercentage: player.serving.knockoutPercentage?.toFixed(3) ?? '0.000',
    acePercentage: player.serving.acePercentage.toFixed(3),
    serveEfficiency: player.serving.efficiencyPercentage?.toFixed(3) ?? '0.000',
    serveTransWinPercentage: player.serving.transitionWinPercentage?.toFixed(3) ?? '0.000',
    aces: player.serving.aces,
    serviceErrors: player.serving.errors
  }
}

const NEW_PLAYER_ID = -1;

const PlayerManagementList: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | undefined>(undefined);
  const [players, setPlayers] = useState<PlayerInfoDto[]>([]);
  const allSeasons = Object.values(Season);
  const [selectedSeason] = useState<Season>(allSeasons[allSeasons.length - 1]);

  useEffect(() => {
    refreshPlayers();
  }, []);

  const refreshPlayers = () => {
    setIsLoading(true);
    getPlayers()
      .then(players => setPlayers(players))
      .catch(e => {
        console.error('Error getting players', e)
        enqueueSnackbar(`Error getting players: ${e}`, {variant: 'error', autoHideDuration: 5000})
      })
      .finally(() => setIsLoading(false));
  }

  const handleRowClick = (params: GridRowParams) => {
    setSelectedPlayerId(params.id as number);
  };

  const handleClosePlayerInfo = () => {
    setSelectedPlayerId(undefined);
  }

  const handleSavePlayerInfo = () => {
    setSelectedPlayerId(undefined);
    refreshPlayers();
  }

  const handleAddNewPlayerClick = () => {
    setSelectedPlayerId(NEW_PLAYER_ID);
  }

  const handleRefreshStats = async () => {
    setIsRefreshing(true);
    try {
      await refreshAllPlayersStats(selectedSeason);
      enqueueSnackbar('Player stats refreshed successfully', {variant: 'success', autoHideDuration: 3000});
      await refreshPlayers();
    } catch (e) {
      console.error('Error refreshing player stats', e);
      enqueueSnackbar(`Error refreshing player stats: ${e}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setIsRefreshing(false);
    }
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%', padding: '25px'}}>
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, padding: 2}}>
        <Button variant="contained" onClick={handleAddNewPlayerClick}>Add New Player</Button>
        {/* <Button variant="contained" color="secondary" onClick={handleRefreshStats} disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing...' : 'Refresh Stats'}
        </Button> */}
      </Box>
      <UrlFilterAwareDataGrid
        columns={playerColumns}
        rowBuilder={playerRowBuilder}
        data={players}
        onRowClick={handleRowClick}
        allFilters={allFilters}
        defaultFilters={[defaultFilter]}
        loading={isLoading}
      />
      <Modal
        open={!!selectedPlayerId}
        onClose={() => handleClosePlayerInfo()}
      >
        <PlayerManagementPlayerComponent playerId={selectedPlayerId === NEW_PLAYER_ID ? undefined : selectedPlayerId} onClose={() => handleClosePlayerInfo()} onSave={() => handleSavePlayerInfo()}/>
      </Modal>
    </Box>
  )
};

export default PlayerManagementList;
